import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import { Avatar } from '../../components/shared/Avatar';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import gsap from 'gsap';

const assets = {
  highlight: 'assets/ui/shared/frames/avatar_border_green.png',
  checkmark: 'assets/ui/shared/icons/checkmark.png',
};

const wrapperSize = 136;
const highlightSize = 130;
const avatarSize = 120;
const duration = 0.3;

export default class EditProfileV2AvatarItem extends FlexBoxView {
  private checkmark: ImageScaleView;
  private highlight: ImageScaleView;
  private _url: string;

  public static get assets() {
    return Object.values(assets);
  }

  public get url() {
    return this._url;
  }

  constructor({
    superview,
    url,
    onClick,
  }: {
    superview: View;
    url: string;
    onClick: () => Promise<void>;
  }) {
    super({
      superview,
      width: wrapperSize,
      height: wrapperSize,
      flex: {
        width: wrapperSize,
        height: wrapperSize,
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: 9,
      },
    });

    this._url = url;

    const wrapper = new View({
      superview: this,
      width: wrapperSize,
      height: wrapperSize,
    });

    const highlightOffset = (wrapperSize - highlightSize) * 0.5;

    this.highlight = new ImageScaleView({
      superview: wrapper,
      image: assets.highlight,
      width: highlightSize,
      height: highlightSize,
      x: highlightOffset,
      y: highlightOffset,
      centerAnchor: true,
    });

    const avatarOffset = highlightOffset + (highlightSize - avatarSize) * 0.5;

    new Avatar({
      superview: wrapper,
      image: url,
      width: avatarSize,
      height: avatarSize,
      centerOnOrigin: false,
      x: avatarOffset,
      y: avatarOffset,
      onClick,
      tracking: {
        feature: 'profile',
        $subFeature: 'popup-editProfile-avatar-selected',
      },
    });

    this.checkmark = new ImageScaleView({
      superview: wrapper,
      image: assets.checkmark,
      width: 49,
      height: 42,
      x: wrapperSize - 47,
      y: highlightSize - 39,
      centerAnchor: true,
    });
  }

  public updateSelected(url: string, instant = false) {
    const selected = this.url === url;

    if (instant) {
      this.highlight.updateOpts({
        visible: selected,
        scale: selected ? 1 : 0.92,
      });

      this.checkmark.updateOpts({
        visible: selected,
        scale: selected ? 1 : 0,
      });

      return;
    }

    gsap
      .timeline({
        onStart: () => {
          if (selected) {
            this.highlight.updateOpts({ visible: true });
            this.checkmark.updateOpts({ visible: true });
          }
        },
        onComplete: () => {
          if (!selected) {
            this.highlight.updateOpts({ visible: false });
            this.checkmark.updateOpts({ visible: false });
          }
        },
      })
      .to(this.highlight.style, {
        duration: duration * (selected ? 1 : 0.5),
        scale: selected ? 1 : 0.92,
        ease: 'quad.inOut',
        overwrite: true,
      })
      .to(
        this.checkmark.style,
        {
          duration: duration * (selected ? 1 : 0.5),
          scale: selected ? 1 : 0,
          ease: selected ? 'back.out' : 'back.in',
          overwrite: true,
        },
        '<',
      );
  }
}
